import React, { useEffect, useState } from 'react'
import { useFetchFacilitationist, useFacilitationDetails } from './api'
import { FiDownload, FiEye } from 'react-icons/fi'
import { BsThreeDotsVertical } from 'react-icons/bs'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'

const DownloadIcon = FiDownload as React.ElementType
const EyeIcon = FiEye as React.ElementType
const MoreIcon = BsThreeDotsVertical as React.ElementType

interface FacilitationItem {
  id: number
  social_category: string
  age: number
  training_names: string
  enterprise_name: string
  business_type: string
  service_type: string
  status: string
}

const tableFields: { label: string; key: keyof FacilitationItem }[] = [
  { label: 'CUSTOMER ID', key: 'id' },
  { label: 'Social Category', key: 'social_category' },
  { label: 'Age', key: 'age' },
  { label: 'Training Names', key: 'training_names' },
  { label: 'Enterprise Name', key: 'enterprise_name' },
  { label: 'Business Type', key: 'business_type' },
  { label: 'Service Type', key: 'service_type' },
  { label: 'Status', key: 'status' },
]

const FacilitationDetail: React.FC<{ id: string; onBack: () => void }> = ({
  id,
  onBack,
}) => {
  const { data, isLoading, isError } = useFacilitationDetails(id)
  const facilitation = data?.data

  const fields = [
    { label: 'Customer No', value: facilitation?.id },
    { label: 'Customer Type', value: facilitation?.service_type },
    { label: 'Address', value: facilitation?.enterprise_address },
    { label: 'Email', value: facilitation?.user?.email },
    { label: 'Phone Number', value: facilitation?.user?.mobile },
    { label: 'Gender', value: facilitation?.gender },
    { label: 'Social Category', value: facilitation?.social_category },
    { label: 'Age', value: facilitation?.age },
    { label: 'Training Names', value: facilitation?.training_names },
    { label: 'Training Period', value: facilitation?.training_period },
    { label: 'Enterprise Name', value: facilitation?.enterprise_name },
    { label: 'Business Type', value: facilitation?.business_type },
    { label: 'Business Activity', value: facilitation?.business_activity },
    { label: 'Products/Services', value: facilitation?.products_or_services },
    { label: 'Service Description', value: facilitation?.service_description },
    { label: 'Status', value: facilitation?.status },
  ]

  const downloadDetailPDF = () => {
    if (!facilitation) return
    const doc = new jsPDF()
    doc.setFontSize(14)
    doc.text(`Facilitation Detail - ID: ${facilitation.id}`, 14, 20)

    const tableData: (string | number)[][] = fields.map((f) => {
      let value: string | number = f.value ?? 'N/A'
      if (typeof value !== 'string' && typeof value !== 'number') {
        value = 'N/A'
      }
      return [f.label, value]
    })

    autoTable(doc, {
      startY: 30,
      head: [['Field', 'Value']],
      body: tableData,
      styles: { fontSize: 10 },
    })

    doc.save(`Facilitation_${facilitation.id}.pdf`)
  }
  if (isLoading) return <p className="p-4">Loading...</p>
  if (isError || !facilitation)
    return <p className="p-4 text-red-500">Failed to load details.</p>

  return (
    <div className="p-2">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold text-emerald-600 bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 hover:shadow-md transition-all duration-200"
        >
          ← Back to List
        </button>
        <button
          onClick={downloadDetailPDF}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 shadow-md hover:shadow-lg hover:scale-[1.03] active:scale-[0.97] transition-all duration-300"
        >
          <DownloadIcon className="w-4 h-4" />
          Download PDF
        </button>
      </div>

      <div className="bg-white rounded-2xl shadow-[0_2px_16px_rgba(0,0,0,0.06)] border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 bg-gradient-to-r from-emerald-50/80 via-teal-50/50 to-transparent border-b border-emerald-100/60">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-md shadow-emerald-200">
              <span className="text-white text-sm">🤝</span>
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">
                Facilitation Details
              </h3>
              <p className="text-xs text-gray-500">
                Request ID: {facilitation?.id}
              </p>
            </div>
          </div>
        </div>
        <div className="p-6">
          <div className="grid gap-3 sm:grid-cols-2">
            {fields.map((field, index) => (
              <div
                key={index}
                className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 p-3 rounded-xl bg-gray-50/80 border border-gray-100 hover:bg-emerald-50/40 transition-colors duration-200"
              >
                <div className="sm:w-2/5 text-xs font-bold text-gray-500 uppercase tracking-wider">
                  {field.label}
                </div>
                <div className="sm:w-3/5 text-sm font-semibold text-gray-900">
                  {field.value || 'N/A'}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

interface FacilitationListProps {
  onDataReady?: (data: FacilitationItem[]) => void
  perPage?: number
  setPerPage?: (val: number) => void
}

const FacilitationList: React.FC<FacilitationListProps> = ({
  onDataReady,
  perPage,
  setPerPage,
}) => {
  const [page, setPage] = useState(1)
  const [selectedFacilitationId, setSelectedFacilitationId] = useState<
    number | null
  >(null)
  const {
    data: response,
    isLoading,
    isError,
  } = useFetchFacilitationist({ page, page_size: perPage || 10 })
  const facilitations: FacilitationItem[] = response?.data || []
  const totalPages = response?.meta?.last_page || 1
  const [activeRowId, setActiveRowId] = useState<number | null>(null)

  // ✅ Send data to parent if needed
  useEffect(() => {
    if (onDataReady) onDataReady(facilitations)
  }, [facilitations, onDataReady])

  const downloadListPDF = () => {
    if (facilitations.length === 0) return
    const doc = new jsPDF()
    doc.setFontSize(14)
    doc.text('Facilitation List', 14, 20)

    const tableData: (string | number)[][] = facilitations.map((f) =>
      tableFields.map((field) => {
        const value = f[field.key]
        return typeof value === 'string' || typeof value === 'number'
          ? value
          : 'N/A'
      })
    )

    autoTable(doc, {
      startY: 30,
      head: [tableFields.map((f) => f.label)],
      body: tableData,
      styles: { fontSize: 10 },
    })

    doc.save('Facilitation_List.pdf')
  }
  if (isLoading)
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-4">
        <div className="w-12 h-12 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin" />
        <p className="text-gray-500 text-sm font-medium">
          Loading facilitation services...
        </p>
      </div>
    )
  if (isError)
    return (
      <div className="text-center py-16">
        <div className="w-16 h-16 mx-auto mb-4 bg-red-50 rounded-full flex items-center justify-center">
          <span className="text-3xl">⚠️</span>
        </div>
        <p className="text-red-500 font-medium">Error loading data.</p>
      </div>
    )
  if (facilitations.length === 0)
    return (
      <div className="text-center py-16">
        <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
          <span className="text-3xl">🤝</span>
        </div>
        <p className="text-gray-500 font-medium">No Data Found</p>
      </div>
    )

  if (selectedFacilitationId) {
    return (
      <FacilitationDetail
        id={String(selectedFacilitationId)}
        onBack={() => {
          setSelectedFacilitationId(null)
          setActiveRowId(null)
        }}
      />
    )
  }

  return (
    <div className="mt-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-50 border border-emerald-100">
          <span className="w-2 h-2 rounded-full bg-emerald-500" />
          <p className="text-sm text-emerald-700 font-semibold">
            {facilitations.length} record
            {facilitations.length !== 1 ? 's' : ''} on this page
          </p>
        </div>
        <button
          onClick={downloadListPDF}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold text-white bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 shadow-[0_4px_14px_rgba(16,185,129,0.35)] hover:shadow-[0_6px_18px_rgba(16,185,129,0.45)] hover:scale-[1.03] active:scale-[0.97] transition-all duration-300"
        >
          <DownloadIcon className="w-4 h-4" />
          Download PDF
        </button>
      </div>

      {/* Table card */}
      <div className="bg-white rounded-2xl shadow-[0_2px_16px_rgba(0,0,0,0.06)] border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-gradient-to-r from-emerald-50 via-teal-50/60 to-transparent border-b border-emerald-100">
                {tableFields.map((field) => (
                  <th
                    key={field.key}
                    className="px-5 py-3.5 text-left text-xs font-bold text-emerald-700 uppercase tracking-wider"
                  >
                    {field.label}
                  </th>
                ))}
                <th className="px-5 py-3.5 text-left text-xs font-bold text-emerald-700 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {facilitations.map((item) => (
                <tr
                  key={item.id}
                  className="border-b border-gray-50 hover:bg-emerald-50/40 transition-colors duration-200"
                >
                  {tableFields.map((field) => (
                    <td key={field.key} className="px-5 py-3.5">
                      {field.key === 'id' ? (
                        <span className="inline-flex items-center justify-center min-w-[2rem] h-7 px-2 rounded-lg bg-gray-100 text-gray-600 text-xs font-bold">
                          {item[field.key]}
                        </span>
                      ) : field.key === 'status' ? (
                        <span
                          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ${
                            item.status === 'confirm' ||
                            item.status === 'approved'
                              ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                              : item.status === 'reject' ||
                                  item.status === 'rejected'
                                ? 'bg-rose-50 text-rose-700 border border-rose-200'
                                : 'bg-amber-50 text-amber-700 border border-amber-200'
                          }`}
                        >
                          <span
                            className={`w-1.5 h-1.5 rounded-full ${
                              item.status === 'confirm' ||
                              item.status === 'approved'
                                ? 'bg-emerald-500'
                                : item.status === 'reject' ||
                                    item.status === 'rejected'
                                  ? 'bg-rose-500'
                                  : 'bg-amber-500'
                            }`}
                          />
                          {item[field.key]}
                        </span>
                      ) : (
                        <span className="text-gray-700 font-medium">
                          {item[field.key]}
                        </span>
                      )}
                    </td>
                  ))}
                  <td className="px-5 py-3.5">
                    {activeRowId === item.id ? (
                      <button
                        onClick={() => setSelectedFacilitationId(item.id)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-emerald-600 bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 transition-colors duration-200"
                      >
                        <EyeIcon className="w-3.5 h-3.5" />
                        View
                      </button>
                    ) : (
                      <button
                        onClick={() => setActiveRowId(item.id)}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-gray-500 bg-gray-50 border border-gray-200 hover:bg-gray-100 hover:text-gray-700 transition-colors duration-200"
                        aria-label="Show actions"
                      >
                        <MoreIcon className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex justify-between items-center gap-3 mt-6 px-4">
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-500">Per Page:</label>
          <select
            value={perPage || 10}
            onChange={(e) => {
              const size = Number(e.target.value)
              setPage(1)
              setPerPage?.(size)
            }}
            className="text-xs border border-gray-300 rounded px-2 py-1 bg-white"
          >
            {[5, 10, 20, 50, 100].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
            disabled={page === 1}
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold border transition-all duration-200 ${
              page === 1
                ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                : 'bg-white text-emerald-600 border-emerald-200 hover:bg-emerald-50 hover:shadow-md'
            }`}
          >
            ← Prev
          </button>
          <div className="flex items-center gap-1.5">
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(
                (p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1
              )
              .map((p, idx, arr) => (
                <React.Fragment key={p}>
                  {idx > 0 && arr[idx - 1] !== p - 1 && (
                    <span className="text-gray-400 text-xs px-0.5">…</span>
                  )}
                  <button
                    onClick={() => setPage(p)}
                    className={`w-9 h-9 rounded-xl text-sm font-bold transition-all duration-200 ${
                      p === page
                        ? 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow-lg shadow-emerald-200'
                        : 'bg-white text-gray-600 border border-gray-200 hover:bg-emerald-50 hover:text-emerald-600'
                    }`}
                  >
                    {p}
                  </button>
                </React.Fragment>
              ))}
          </div>
          <button
            onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
            disabled={page === totalPages}
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold border transition-all duration-200 ${
              page === totalPages
                ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                : 'bg-white text-emerald-600 border-emerald-200 hover:bg-emerald-50 hover:shadow-md'
            }`}
          >
            Next →
          </button>
        </div>
      </div>
    </div>
  )
}

export default FacilitationList
